"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { useLang } from "@/components/LangProvider";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type FieldErrors = Partial<Record<"name" | "email" | "message", string>>;

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, options: Record<string, unknown>) => string;
      reset: (id?: string) => void;
    };
  }
}

export function ContactForm() {
  const { t } = useLang();
  const f = t.contactPage.form;

  const [submitting, setSubmitting] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [generalError, setGeneralError] = useState<string | null>(null);

  const turnstileRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetId = useRef<string | null>(null);
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

  // Turnstile は script の読み込み後に手動で描画する。
  // 自動描画に任せるとクライアント遷移で二重描画されることがある
  useEffect(() => {
    if (!siteKey || !turnstileRef.current || turnstileWidgetId.current) return;

    const render = () => {
      if (!window.turnstile || !turnstileRef.current) return;
      turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
        sitekey: siteKey,
        language: "ja",
      });
    };

    if (window.turnstile) {
      render();
      return;
    }

    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.onload = render;
    document.head.appendChild(script);
  }, [siteKey]);

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    const values = {
      name: String(data.get("name") ?? "").trim(),
      company: String(data.get("company") ?? "").trim(),
      email: String(data.get("email") ?? "").trim(),
      phone: String(data.get("phone") ?? "").trim(),
      inquiryType: String(data.get("inquiryType") ?? ""),
      message: String(data.get("message") ?? "").trim(),
    };

    // サーバー側でも同じ検証をする。ここは体感を良くするためのもの
    const errors: FieldErrors = {};
    if (!values.name) errors.name = f.errors.name;
    if (!values.email) errors.email = f.errors.email;
    else if (!EMAIL_PATTERN.test(values.email)) errors.email = f.errors.emailFormat;
    if (!values.message) errors.message = f.errors.message;

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setGeneralError(null);
      return;
    }

    setFieldErrors({});
    setGeneralError(null);
    setSubmitting(true);

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...values,
          companyName: values.company,
          turnstileToken: String(data.get("cf-turnstile-response") ?? ""),
        }),
      });

      if (response.ok) {
        setSucceeded(true);
        return;
      }

      const json = (await response.json().catch(() => ({}))) as {
        reason?: string;
        errors?: Record<string, keyof typeof f.errors>;
      };

      if (json.errors) {
        setFieldErrors(
          Object.fromEntries(
            Object.entries(json.errors).map(([key, code]) => [key, f.errors[code] ?? code]),
          ) as FieldErrors,
        );
        return;
      }

      setGeneralError(
        json.reason === "rate_limited"
          ? f.errors.rateLimited
          : json.reason === "turnstile"
            ? f.errors.turnstile
            : f.errors.failed,
      );
    } catch {
      setGeneralError(f.errors.failed);
    } finally {
      setSubmitting(false);
      window.turnstile?.reset(turnstileWidgetId.current ?? undefined);
    }
  }

  if (succeeded) {
    return (
      <div
        role="status"
        className="border border-rule bg-paper px-6 py-12 text-center"
      >
        <h3
          className="font-display text-deep m-0"
          style={{ fontSize: 20, letterSpacing: "0.04em" }}
        >
          {f.successTitle}
        </h3>
        <p
          className="text-[#4a4a4a] font-sans whitespace-pre-line"
          style={{ fontSize: 14, lineHeight: 2, marginTop: 12 }}
        >
          {f.successBody}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="grid gap-6">
      {generalError && (
        <p
          role="alert"
          className="border border-[#d8b4b4] bg-[#fdf4f4] px-4 py-3 text-center font-sans text-[#9b3b3b]"
          style={{ fontSize: 13, lineHeight: 1.8 }}
        >
          {generalError}
        </p>
      )}

      <Field label={f.name} htmlFor="name" required error={fieldErrors.name}>
        <input
          id="name"
          name="name"
          type="text"
          autoComplete="name"
          placeholder={f.namePlaceholder}
          className={inputClass}
        />
      </Field>

      <Field label={f.company} htmlFor="company" optionalLabel={f.optional}>
        <input
          id="company"
          name="company"
          type="text"
          autoComplete="organization"
          placeholder={f.companyPlaceholder}
          className={inputClass}
        />
      </Field>

      <Field label={f.email} htmlFor="email" required error={fieldErrors.email}>
        <input
          id="email"
          name="email"
          type="email"
          autoComplete="email"
          placeholder={f.emailPlaceholder}
          className={inputClass}
        />
      </Field>

      <Field label={f.phone} htmlFor="phone" optionalLabel={f.optional}>
        <input
          id="phone"
          name="phone"
          type="tel"
          autoComplete="tel"
          placeholder={f.phonePlaceholder}
          className={inputClass}
        />
      </Field>

      <Field label={f.type} htmlFor="inquiryType" optionalLabel={f.optional}>
        <select id="inquiryType" name="inquiryType" className={inputClass} defaultValue="">
          <option value="">—</option>
          {Object.entries(f.typeOptions).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </Field>

      <Field label={f.message} htmlFor="message" required error={fieldErrors.message}>
        <textarea
          id="message"
          name="message"
          rows={6}
          placeholder={f.messagePlaceholder}
          className={`${inputClass} resize-none`}
        />
      </Field>

      {siteKey && <div ref={turnstileRef} />}

      {/* **個人情報を取る画面から取扱いに辿れるようにする。**
          フッターにしか導線が無いと、入力中の人には見えない */}
      <p
        className="text-mute font-sans m-0"
        style={{ fontSize: 12, lineHeight: 1.9 }}
      >
        {f.privacyNotePrefix}
        <Link
          href="/privacy-policy"
          className="underline underline-offset-4 decoration-rule hover:text-accent-forest hover:decoration-accent-forest transition-colors"
        >
          {f.privacyNoteLink}
        </Link>
        {f.privacyNoteSuffix}
      </p>

      <div className="pt-2">
        <button
          type="submit"
          disabled={submitting}
          className="w-full px-8 py-4 font-sans font-medium uppercase text-white bg-deep border border-deep hover:opacity-85 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed md:w-auto md:min-w-[220px]"
          style={{ fontSize: 11, letterSpacing: "0.22em" }}
        >
          {submitting ? f.submitting : f.submit}
        </button>
      </div>
    </form>
  );
}

const inputClass =
  "w-full bg-white border border-rule px-3 py-3 font-sans text-[#3a3a3a] focus:outline-none focus:border-deep transition-colors";

function Field({
  label,
  htmlFor,
  required,
  optionalLabel,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  required?: boolean;
  optionalLabel?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        htmlFor={htmlFor}
        className="block font-display text-deep mb-2"
        style={{ fontSize: 13, letterSpacing: "0.06em" }}
      >
        {label}
        {required ? (
          <span className="text-[#9b3b3b] ml-1">*</span>
        ) : optionalLabel ? (
          <span className="text-mute ml-2 font-sans" style={{ fontSize: 10.5 }}>
            {optionalLabel}
          </span>
        ) : null}
      </label>
      {children}
      {error && (
        <p className="text-[#9b3b3b] font-sans mt-1.5" style={{ fontSize: 12 }}>
          {error}
        </p>
      )}
    </div>
  );
}
